import React, { Dispatch, useMemo, useState, type SetStateAction } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import {
  ArrowLeft,
  CheckmarkOutline,
  CloseOutline,
  Information,
} from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import { Suggestions } from '@mint-sdk/api-recommendations';
import { useSetAtom } from 'jotai';

import { useBulkPublishMode } from '../../../contexts/BulkPublishModeContext.jsx';
import { useSuggestionFilter } from '../../../hooks/useSuggestionFilter.js';
import useSuggestionPermissions from '../../../hooks/useSuggestionPermissions.js';
import {
  useAcceptSuggestion,
  usePublishSuggestion,
  useRejectSuggestion,
  useRollbackSuggestion,
  useSuggestionListOptimisticUpdate,
} from '../../../queries/suggestions.js';
import { activeSuggestionTabAtom } from '../../../store/index.js';
import { SuggestionFilterActions } from '../../../store/suggestion/suggestionFilter.js';
import { SuggestionTabs } from '../../../types/suggestion.js';
import { suggestionTabs } from '../../../utils/getTabs.js';
import RejectReason from '../../atoms/RejectReason/RejectReason.jsx';
import SeparateStatusChip from '../../atoms/SeparateStatusChip/SeparateStatusChip.jsx';
import SuggestionProcessingModal from '../../atoms/SuggestionReworkModal/SuggestionReworkModal.jsx';
import BulkPublishToggle from '../../molecules/BulkPublishToggle/BulkPublishToggle.jsx';
import {
  AcceptDialog,
  PublishDialog,
  RejectDialog,
  RollbackDialog,
} from '../../molecules/Dialogs/index.js';
import KpiWidget from '../../molecules/KpiWidget/KpiWidget.jsx';
import { PublishSuggestion } from '../../molecules/PublishSuggestion/PublishSuggestion.jsx';
import { SuggestionDateStatus } from '../../molecules/SuggestionDateStatus/SuggestionDateStatus.jsx';

type SuggestionHeadlineProps = {
  suggestion: Suggestions.SuggestionResponseType;
  isEditingMode: boolean;
  isGenerating: boolean;
  updateSuggestion: Dispatch<
    SetStateAction<Suggestions.SuggestionResponseType>
  >;
  closeGenerationModal: () => void;
};

type DialogState = 'accept' | 'reject' | 'publish' | 'rollback' | null;

const SuggestionHeadline = ({
  suggestion,
  isEditingMode,
  isGenerating,
  updateSuggestion,
  closeGenerationModal,
}: SuggestionHeadlineProps) => {
  const isBulkPublishMode = useBulkPublishMode();
  const { t } = useTranslation('@mint-lib/recommendations-circuit');
  const queryClient = useQueryClient();
  const { updateSuggestionList } = useSuggestionListOptimisticUpdate();
  const setActiveTab = useSetAtom(activeSuggestionTabAtom);

  const { status, journeyName } = suggestion;

  const { isAcceptRejectAllowed, isProcessingAllowed } =
    useSuggestionPermissions();

  const { selectedSuggestionFilter, setSuggestionFilter } =
    useSuggestionFilter();

  const [isProcessingModalToggled, setIsProcessingModalToggled] =
    useState<boolean>(true);
  const [isProcessingModalOpen, setIsProcessingModalOpen] =
    useState<boolean>(false);
  const handleToggleProcessingModal = () =>
    setIsProcessingModalToggled((prevState) => !prevState);
  const handleProcessingModalClose = () => {
    setIsProcessingModalOpen(false);
  };

  const [dialog, setDialog] = useState<DialogState>(null);

  const {
    mutate: rollbackReco,
    isPending: isLoadingRollback,
    isError: isErrorRollback,
    error: errorRollback,
    isSuccess: isSuccessRollback,
  } = useRollbackSuggestion();

  const { mutate: acceptReq, isPending: isLoadingAccept } =
    useAcceptSuggestion();
  const { mutate: publishReq, isPending: isLoadingPublish } =
    usePublishSuggestion();
  const { mutate: rejectReq, isPending: isLoadingReject } =
    useRejectSuggestion();
  const onChangeTab = (tab: SuggestionTabs) => {
    setActiveTab(tab);
    setSuggestionFilter({
      type: SuggestionFilterActions.RESET,
      data: tab.toUpperCase(),
    });
  };

  const onRejectSuggestion = (reason: string) => () => {
    rejectReq(
      { id: suggestion.id, reason },
      {
        onSuccess: (data) => {
          setDialog(null);

          queryClient.invalidateQueries({
            queryKey: Suggestions.listInfiniteQueryOptions({
              params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
            }).queryKey,
          });
          onChangeTab(suggestionTabs.REJECTED);

          updateSuggestion(data);

          enqueueSnackbar({
            variant: 'success',
            autoHideDuration: 4000,
            title: t('Suggestion rejected'),
            subTitle: t('You rejected the suggestion for {{journeyName}}', {
              journeyName: suggestion.journeyName,
            }),
          });
        },
        onError: () => {
          setDialog(null);

          enqueueSnackbar({
            variant: 'error',
            autoHideDuration: 5000,
            title: t('Error'),
            removable: true,
            subTitle: t(
              'Something went wrong when you rejected the suggestion.',
            ),
          });
        },
      },
    );
  };

  const onAcceptSuggestion = () => {
    acceptReq(
      { suggestionId: suggestion.id },
      {
        onSuccess: async () => {
          setDialog(null);
          queryClient.invalidateQueries({
            queryKey: Suggestions.listInfiniteQueryOptions({
              params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
            }).queryKey,
          });
          onChangeTab(suggestionTabs.ACCEPTED);

          const currentSuggestion = await queryClient.fetchQuery(
            Suggestions.getSuggestion({ urlParams: { id: suggestion.id } }),
          );

          updateSuggestion(currentSuggestion);

          if (currentSuggestion.status === Suggestions.Statuses.ERROR) {
            onChangeTab(suggestionTabs.ERROR);
          }

          enqueueSnackbar({
            variant: 'success',
            autoHideDuration: 5000,

            removable: true,
            title: t('Suggestion accepted'),
            subTitle: t(
              'You accepted the suggestion and updated the {{ journeyName }} version.',
              {
                journeyName: suggestion.journeyName,
              },
            ),
          });
        },
        onError: () => {
          setDialog(null);

          enqueueSnackbar({
            variant: 'error',
            autoHideDuration: 5000,
            removable: true,
            title: t('Error'),
            subTitle: t(
              'Something went wrong when you accepted the recommendation',
            ),
          });
        },
      },
    );
  };

  const onPublishSuggestion = () => {
    publishReq(suggestion, {
      onSuccess: (data) => {
        const publishedSuggestion = data.items.find(
          (item) => item.id === suggestion.id,
        ) ?? { ...suggestion, enabled: true };

        updateSuggestionList(publishedSuggestion, selectedSuggestionFilter); // Optimistic update
        updateSuggestion(publishedSuggestion);

        queryClient.invalidateQueries({
          queryKey: Suggestions.listInfiniteQueryOptions({
            params: { ...selectedSuggestionFilter, page: 1, pageSize: 10 },
          }).queryKey,
          exact: true,
        });
        setDialog(null);

        enqueueSnackbar({
          variant: 'success',
          autoHideDuration: 5000,
          title: t('Suggestion published'),
          removable: true,
          subTitle: t('You published the suggestion for {{ journeyName }}', {
            journeyName: suggestion.journeyName,
          }),
        });
      },

      onError: () => {
        setDialog(null);

        enqueueSnackbar({
          variant: 'error',
          autoHideDuration: 5000,
          title: t('Error'),
          removable: true,
          subTitle: t(
            'Something went wrong when you published the recommendation',
          ),
        });
      },
    });
  };

  const handleRevert = () => {
    setIsProcessingModalOpen(true);
    closeGenerationModal();
    rollbackReco(suggestion.id);
    setDialog(null);
  };

  const shouldDisableRecoActionButtons = useMemo(
    () =>
      suggestion?.status !== Suggestions.Statuses.ACTIVE || // FYI: If suggestion in reworking status - should disable buttons
      !suggestion.enabled || // !isPublished
      isGenerating ||
      isEditingMode ||
      isLoadingRollback ||
      isSuccessRollback,

    [
      suggestion,
      isEditingMode,
      isGenerating,
      isLoadingRollback,
      isSuccessRollback,
    ],
  );

  return (
    <div className="flex flex-col items-start gap-4">
      {suggestion?.originalId &&
      suggestion.status === Suggestions.Statuses.ACTIVE &&
      isProcessingAllowed ? (
        <div className="flex flex-row gap-1">
          <Button
            variant="plain"
            onClick={() => setDialog('rollback')}
            aria-label="Back to original version"
            disabled={shouldDisableRecoActionButtons}
          >
            <ArrowLeft
              className={cn(
                'size-4',
                shouldDisableRecoActionButtons
                  ? 'fill-text-disabled'
                  : 'fill-tertiary-main',
              )}
            />
            {t('Back to original version')}
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Information
                className={cn(
                  'size-4',
                  shouldDisableRecoActionButtons
                    ? 'fill-text-disabled'
                    : 'fill-tertiary-main',
                )}
              />
            </TooltipTrigger>
            <TooltipContent
              sideOffset={10}
              side="top"
              align="center"
              arrow={false}
            >
              {t(
                'Revert to original suggestion. Current changes will be lost.',
              )}
            </TooltipContent>
          </Tooltip>
        </div>
      ) : null}
      <div className="flex w-full flex-row justify-between">
        <div className="flex flex-col gap-4">
          <div className="flex flex-row items-center gap-2">
            <p
              className={cn('break-all', typographyVariants({ variant: 'h3' }))}
            >
              {journeyName}
            </p>
            {suggestion?.originalId ? (
              <SeparateStatusChip type="ReworkedHeadline" />
            ) : null}
          </div>
        </div>
        {status === Suggestions.Statuses.ACTIVE ? (
          <div className="flex flex-row gap-2">
            {/*FYI: Hiding accept/reject for AIRFRANCE company is request from business https://mint-ai.atlassian.net/browse/PDT-73001*/}
            {isAcceptRejectAllowed && (
              <>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  disabled={shouldDisableRecoActionButtons}
                  onClick={() => setDialog('reject')}
                >
                  <CloseOutline className="size-4" />
                  {t('Reject')}
                </Button>
                <Button
                  variant="contained"
                  disabled={shouldDisableRecoActionButtons}
                  size="small"
                  onClick={() => setDialog('accept')}
                >
                  <CheckmarkOutline className="size-4" />
                  {t('Accept')}
                </Button>
              </>
            )}
            {isBulkPublishMode ? (
              <BulkPublishToggle />
            ) : (
              <PublishSuggestion
                isEnabledSuggestion={suggestion.enabled}
                onOpenPublishDialog={() => setDialog('publish')}
              />
            )}
          </div>
        ) : (
          <SuggestionDateStatus suggestion={suggestion} status={status} />
        )}

        {!isBulkPublishMode && dialog === 'publish' && (
          <PublishDialog
            isLoading={isLoadingPublish}
            onCloseDialog={() => setDialog(null)}
            onPublish={onPublishSuggestion}
          />
        )}
        {dialog === 'accept' && (
          <AcceptDialog
            isLoading={isLoadingAccept}
            onCloseDialog={() => setDialog(null)}
            onAccept={() => onAcceptSuggestion()}
          />
        )}
        {dialog === 'reject' && (
          <RejectDialog
            isLoading={isLoadingReject}
            onCloseDialog={() => setDialog(null)}
            onReject={onRejectSuggestion}
          />
        )}
        {dialog === 'rollback' && (
          <RollbackDialog
            onClose={() => setDialog(null)}
            handleRevert={handleRevert}
            isLoadingRollback={isLoadingRollback}
          />
        )}
      </div>
      {suggestion.status === Suggestions.Statuses.REJECTED &&
        suggestion.reason && (
          <RejectReason
            reason={suggestion.reason}
            decidedBy={suggestion.decidedBy}
            date={suggestion.decisionTime}
          />
        )}
      <div className="mt-1 flex h-full w-full flex-col gap-4">
        <KpiWidget suggestion={suggestion} />
      </div>
      {isProcessingModalOpen && (
        <SuggestionProcessingModal
          isDrawerOpen={false}
          mode="rollback"
          isError={isErrorRollback}
          error={errorRollback}
          isSuccess={isSuccessRollback}
          onExpandButtonClick={handleToggleProcessingModal}
          onCloseButtonClick={handleProcessingModalClose}
          isModalExpanded={isProcessingModalToggled}
        />
      )}
    </div>
  );
};

export default SuggestionHeadline;
